import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import TemuOpsListToolbar, { downloadCsv } from '@/temu-platform/components/TemuOpsListToolbar';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import TemuTemplateScopeTabs from '@/temu-platform/components/TemuTemplateScopeTabs';
import TemuGatewayRegionTabs, { TEMU_GATEWAY_REGION_ALL } from '@/temu-platform/components/TemuGatewayRegionTabs';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import ModelTemplateCreateModal from '@/temu-platform/modules/templates/ModelTemplateCreateModal';
import FreightTemplateDetailModal from '@/temu-platform/modules/templates/FreightTemplateDetailModal';
import FreightTemplateSitesModal from '@/temu-platform/modules/templates/FreightTemplateSitesModal';
import SyncPlatformJsonModal from '@/temu-platform/components/SyncPlatformJsonModal';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { useGuardedRefresh } from '@/temu-platform/hooks/useGuardedRefresh';
import { useTemuPageShopScope, stackShopDimensionTabs } from '@/temu-platform/hooks/useTemuPageShopScope';
import useTemuTemplateScope from '@/temu-platform/hooks/useTemuTemplateScope';
import { TEMU_TEMPLATE_SCOPES, isTeamTemplateScope } from '@/temu-platform/constants/temuTemplateScope';
import { formatBeijingDateTime } from '@/temu-platform/utils/formatBeijingDateTime';
import { cellDisplayTitle } from '@/temu-platform/utils/cellDisplayTitle';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import { fetchAllPages } from '@/temu-platform/utils/temuExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import { dumpKindForSyncKind } from '@/temu-platform/utils/temuPlatformJsonConsole';
import { temuWarehouseManagementTypeLabel } from '@/temu-platform/utils/temuWarehouseManagementType';
import { isAllShopsScope } from '@/utils/platformShopScope';
import { formatShopSyncSectionError } from '@/temu-platform/utils/formatTemuSyncError';
import { temuPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { fixedUi } from '@/i18n/fixedUi';
import i18n from '@/i18n';

/** 平台 modelType：0 成衣 / 1 鞋类 */

const DEFAULT_MODEL_TYPE = '0';

/** 运费/发货仓本地缓存按部署区筛选，不依赖销售订单区 Token 开站结果 */
const LOCAL_CACHE_GATEWAY_REGIONS = [
  { code: 'US', enabled: true },
  { code: 'EU', enabled: true },
  { code: 'GLOBAL', enabled: true },
];

function syncKindForPage(kind) {
  if (kind === 'warehouse') return 'warehouses';
  if (kind === 'model') return 'models';
  return 'freightTemplates';
}

function syncErrorKeyForPage(kind) {
  return syncKindForPage(kind);
}

function rowKeyOf(kind, row, { official = false } = {}) {
  if (kind === 'warehouse') {
    return String(row?.warehouseId || row?.id || row?.rowId || '');
  }
  if (kind === 'model') {
    if (official) return String(row?.id || row?.rowId || '');
    return String(row?.modelId || row?.id || row?.rowId || '');
  }
  return String(row?.freightTemplateId || row?.id || row?.rowId || '');
}

async function resolvePortraitTemuUrl(shopId, portraitUrl) {
  const src = String(portraitUrl || '').trim();
  if (!src) return '';
  if (/kwcdn|temucdn|temu\.com|kwimgs/i.test(src)) return src;
  const resp = await fetch(src, { credentials: 'include' });
  if (!resp.ok) {
    throw new Error(i18n.t('modelLib.avatarDownloadFailed', { ns: 'workbench' }));
  }
  const blob = await resp.blob();
  const ext = (blob.type || '').includes('png') ? 'png' : 'jpg';
  const file = new File([blob], `model-portrait.${ext}`, { type: blob.type || 'image/jpeg' });
  const uploaded = await temuPlatformAPI.uploadProductPic(shopId, file, {
    imageType: 1,
    imageBizType: 0,
  });
  const temuUrl = String(
    uploaded?.temuUrl || uploaded?.url || uploaded?.urls?.[0] || '',
  ).trim();
  if (!temuUrl) {
    throw new Error(i18n.t('modelLib.avatarConvertFailed', { ns: 'workbench' }));
  }
  return temuUrl;
}

function needsGatewayRegionTabs(kind) {
  return kind === 'warehouse' || kind === 'freight';
}

/**
 * 运费模板 / 发货仓：无官方库与新建，仅展示店铺同步缓存，通过「同步更新」拉取平台数据。
 * 模特库：同步缓存 + 平台新建 / 编辑（bg.glo.modelinfo.add / edit）。
 *
 * @param {{
 *   kind: 'freight' | 'warehouse' | 'model',
 *   libraryLabel: string,
 *   idColumnLabel: string,
 *   nameColumnLabel: string,
 *   syncHint?: string,
 *   showPlatformJson?: boolean,
 * }} props
 */
export default function ShopSyncedMaterialListPage({
  kind,
  libraryLabel,
  idColumnLabel,
  nameColumnLabel,
  syncHint: syncHintProp,
  showPlatformJson = false,
}) {
  const { t } = useTranslation('workbench');
  const syncHint = syncHintProp || t('syncedLib.defaultHintTemu');
  const allowModelMutate = kind === 'model';
  const showGatewayRegionTabs = needsGatewayRegionTabs(kind);
  const platformJsonDumpKind = showPlatformJson ? dumpKindForSyncKind(syncKindForPage(kind)) : '';
  const {
    shopDimensionTabs,
    pageShopScope,
    concreteScopeId,
    listReady,
  } = useTemuPageShopScope();

  const shopId = listReady && concreteScopeId ? String(concreteScopeId) : '';
  const needsConcreteShop = isAllShopsScope(pageShopScope);
  const syncKind = syncKindForPage(kind);
  const syncKinds = useMemo(() => [syncKind], [syncKind]);
  const { startJob, onTrackDone, isBusy } = useTemuSyncJobs();
  const syncBusy = Boolean(shopId) && isBusy(syncKind, shopId);
  const {
    scopeTab: modelScopeTab,
    setScopeTab: setModelScopeTab,
    changeScopeTab: changeModelScopeTab,
    canAccessTeam,
    isOfficialTab: modelScopeOfficial,
  } = useTemuTemplateScope({ loadCategories: false, preferredShopId: pageShopScope });

  /** 模特库归属与其他模板库一致：单店铺独享 / 团队店铺共享 / 官方 */
  const isOfficialModelTab = kind === 'model' && modelScopeOfficial;
  const isTeamModelTab = kind === 'model' && isTeamTemplateScope(modelScopeTab);
  const isShopModelTab = kind !== 'model' || (!isOfficialModelTab && !isTeamModelTab);
  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [modelType, setModelType] = useState(DEFAULT_MODEL_TYPE);
  const [gatewayRegion, setGatewayRegion] = useState(TEMU_GATEWAY_REGION_ALL);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [syncedAt, setSyncedAt] = useState('');
  const [untaggedCount, setUntaggedCount] = useState(0);
  const [platformJsonOpen, setPlatformJsonOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copyingOfficialId, setCopyingOfficialId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [freightDetailId, setFreightDetailId] = useState('');
  const [freightSitesRow, setFreightSitesRow] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [exporting, setExporting] = useState(false);

  const selectableKeys = useMemo(
    () => items.map((row) => rowKeyOf(kind, row, { official: isOfficialModelTab })).filter(Boolean),
    [items, kind, isOfficialModelTab],
  );
  const allPageSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const listGatewayRegion = showGatewayRegionTabs
    && gatewayRegion
    && gatewayRegion !== TEMU_GATEWAY_REGION_ALL
    ? gatewayRegion
    : undefined;

  const listTabs = useMemo(() => {
    if (!showGatewayRegionTabs) return shopDimensionTabs;
    return stackShopDimensionTabs(shopDimensionTabs, (
      <TemuGatewayRegionTabs
        shopId={shopId}
        value={gatewayRegion}
        regions={LOCAL_CACHE_GATEWAY_REGIONS}
        onChange={(next) => {
          setGatewayRegion(next || TEMU_GATEWAY_REGION_ALL);
          setPage(1);
          setSelectedKeys(new Set());
          selectedRowsRef.current.clear();
        }}
      />
    ));
  }, [showGatewayRegionTabs, shopDimensionTabs, shopId, gatewayRegion]);

  const load = useCallback(async () => {
    if (!shopId) {
      setItems([]);
      setTotal(0);
      setSyncedAt('');
      setUntaggedCount(0);
      return;
    }
    setLoading(true);
    try {
      let res;
      if (kind === 'warehouse') {
        res = await temuPlatformAPI.getShopWarehouses(shopId, {
          keyword: keyword || undefined,
          paged: true,
          page,
          pageSize,
          gatewayRegion: listGatewayRegion,
        });
      } else if (kind === 'model') {
        if (isTeamModelTab) {
          setItems([]);
          setTotal(0);
          setSyncedAt('');
          setUntaggedCount(0);
          return;
        }
        if (isOfficialModelTab) {
          res = await temuPlatformAPI.listModelTemplates(shopId, {
            scope: 'official',
            keyword: keyword || undefined,
            page,
            pageSize,
          });
          const rawList = Array.isArray(res?.items) ? res.items : [];
          const wantType = Number(modelType);
          const filtered = rawList.filter((row) => {
            const t = row?.modelType ?? row?.payload?.modelType;
            if (t == null || t === '') return true;
            return Number(t) === wantType;
          });
          // 官方列表已在服务端分页；类型过滤后可能变少，仍展示当页过滤结果
          setItems(filtered);
          setTotal(Number(res?.total) || filtered.length);
          setSyncedAt('');
          setUntaggedCount(0);
          return;
        }
        // 模特类型由「成衣 / 鞋类」Tab 下推到后端过滤
        res = await temuPlatformAPI.listShopModels(shopId, {
          keyword: keyword || undefined,
          modelType: modelType === '' || modelType == null ? undefined : Number(modelType),
          page,
          pageSize,
        });
      } else {
        res = await temuPlatformAPI.listShopFreightTemplates(shopId, {
          keyword: keyword || undefined,
          page,
          pageSize,
          gatewayRegion: listGatewayRegion,
        });
      }
      const list = Array.isArray(res?.items)
        ? res.items
        : (Array.isArray(res?.warehouses) ? res.warehouses : []);
      setItems(list);
      setTotal(Number(res?.total) || list.length);
      setSyncedAt(res?.syncedAt || '');
      setUntaggedCount(Number(res?.untaggedCount) || 0);
      if (
        showGatewayRegionTabs
        && listGatewayRegion
        && list.length === 0
        && Number(res?.untaggedCount) > 0
      ) {
        showToast(t('syncedLib.missingRegion'), 'warning');
      }
    } catch (e) {
      setItems([]);
      setTotal(0);
      setUntaggedCount(0);
      showToast(e?.message || t('syncedLib.loadFailed', { name: libraryLabel }), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, kind, keyword, modelType, listGatewayRegion, page, pageSize, libraryLabel, showGatewayRegionTabs, isOfficialModelTab, isTeamModelTab, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, [shopId, kind, keyword, modelType, gatewayRegion, modelScopeTab]);

  useEffect(() => {
    setGatewayRegion(TEMU_GATEWAY_REGION_ALL);
    setPage(1);
    setModelScopeTab(TEMU_TEMPLATE_SCOPES.personal);
  }, [shopId, setModelScopeTab]);

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(needsConcreteShop ? t('syncedLib.pickConcreteSync') : t('returnList.pickShop'), 'warning');
      return;
    }
    try {
      const track = await startJob({
        kind: syncKind,
        shopId,
        label: t('syncedLib.syncJob', { name: libraryLabel }),
        silent: true,
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      setPage(1);
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      await load();

      const errors = result?.view?.errors && typeof result.view.errors === 'object'
        ? result.view.errors
        : {};
      const kindErrKey = syncErrorKeyForPage(kind);
      const kindErr = formatShopSyncSectionError(kindErrKey, errors[kindErrKey]);
      const viewError = String(result?.view?.error || result?.view?.errorMessage || '').trim();
      if (result?.ok && result?.view?.ok !== false && !kindErr && !viewError) {
        showToast(result?.view?.message || t('syncedLib.syncedShort', { name: libraryLabel }), 'success');
      } else if (kindErr) {
        showToast(kindErr, 'warning');
      } else if (!result?.ok && !result?.cancelled) {
        showToast(
          formatShopSyncSectionError(kindErrKey, result?.view?.error || result?.view?.message)
          || result?.view?.error
          || result?.view?.message
          || t('syncedLib.syncFailed'),
          'error',
        );
      }
      if (result?.view?.syncedAt) setSyncedAt(result.view.syncedAt);
    } catch (e) {
      try {
        await load();
      } catch (_) {
        /* ignore */
      }
      showToast(e?.message || t('syncedLib.syncFailed'), 'error');
    }
  }, [shopId, needsConcreteShop, kind, syncKind, libraryLabel, load, startJob, onTrackDone, t]);

  const {
    refresh: guardedSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncing = syncBusy || syncingLocal;

  const toggleOne = (row) => {
    const key = rowKeyOf(kind, row, { official: isOfficialModelTab });
    if (!key) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectedRowsRef.current.delete(key);
      } else {
        next.add(key);
        selectedRowsRef.current.set(key, row);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedKeys((prev) => {
      const next = togglePageSelection(prev, selectableKeys, allPageSelected);
      if (allPageSelected) {
        selectableKeys.forEach((key) => selectedRowsRef.current.delete(key));
      } else {
        items.forEach((row) => {
          const key = rowKeyOf(kind, row, { official: isOfficialModelTab });
          if (key) selectedRowsRef.current.set(key, row);
        });
      }
      return next;
    });
  };

  const resolveSelectedRows = () => (
    [...selectedKeys].map((key) => (
      selectedRowsRef.current.get(key)
      || items.find((row) => rowKeyOf(kind, row, { official: isOfficialModelTab }) === key)
    )).filter(Boolean)
  );

  const mapRowsToCsv = (rows) => {
    let headers;
    let mapped;
    if (kind === 'warehouse') {
      headers = [idColumnLabel, nameColumnLabel, t('warehouseLib.mgmtType'), t('warehouseLib.status'), t('freightLib.colSynced')];
      mapped = rows.map((row) => ({
        [idColumnLabel]: row.warehouseId || '',
        [nameColumnLabel]: row.name || row.warehouseName || '',
        [t('warehouseLib.mgmtType')]: temuWarehouseManagementTypeLabel(row.managementType) || row.managementType || '',
        [t('warehouseLib.status')]: row.warehouseDisable ? t('warehouseLib.disabled') : t('brandLib.available'),
        [t('freightLib.colSynced')]: formatBeijingDateTime(row.lastSyncedAt || row.updatedAt) || '',
      }));
    } else if (kind === 'model') {
      const isShoe = String(modelType) === '1';
      headers = isShoe
        ? [idColumnLabel, nameColumnLabel, t('modelLib.type'), t('modelLib.footLength'), t('modelLib.footWidth'), t('productTemplate.colCreatedAt'), t('productTemplate.colUpdatedAt')]
        : [idColumnLabel, nameColumnLabel, t('modelLib.type'), t('modelLib.height'), t('modelLib.bust'), t('modelLib.waist'), t('modelLib.hip'), t('productTemplate.colCreatedAt'), t('productTemplate.colUpdatedAt')];
      mapped = rows.map((row) => {
        const base = {
          [idColumnLabel]: row.modelId || row.id || '',
          [nameColumnLabel]: row.name || row.modelName || '',
          [t('modelLib.type')]: fixedUi(row.modelTypeLabel || ''),
          [t('productTemplate.colCreatedAt')]: formatBeijingDateTime(row.createdAt),
          [t('productTemplate.colUpdatedAt')]: formatBeijingDateTime(row.lastSyncedAt || row.updatedAt),
        };
        if (isShoe) {
          return { ...base, [t('modelLib.footLength')]: row.footLength || '', [t('modelLib.footWidth')]: row.footWidth || '' };
        }
        return {
          ...base,
          [t('modelLib.height')]: row.height || '',
          [t('modelLib.bust')]: row.bust || '',
          [t('modelLib.waist')]: row.waist || '',
          [t('modelLib.hip')]: row.hip || '',
        };
      });
    } else {
      headers = [idColumnLabel, nameColumnLabel, t('freightLib.colSynced')];
      mapped = rows.map((row) => ({
        [idColumnLabel]: row.freightTemplateId || row.id || '',
        [nameColumnLabel]: row.name || row.templateName || '',
        [t('freightLib.colSynced')]: formatBeijingDateTime(row.lastSyncedAt || row.updatedAt) || '',
      }));
    }
    return { headers, mapped };
  };

  const exportCsvRows = (rows) => {
    if (!rows.length) {
      showToast(t('brandLib.nothingToExport'), 'warning');
      return;
    }
    const { headers, mapped } = mapRowsToCsv(rows);
    downloadCsv(`temu-${kind}-${Date.now()}.csv`, headers, mapped);
    showToast(t('tiktokCatalog.exported', { count: rows.length }), 'success');
  };

  const fetchFilteredRows = useCallback(async () => {
    if (!shopId) return [];
    const all = await fetchAllPages(async (pageNo, size) => {
      let res;
      if (kind === 'warehouse') {
        res = await temuPlatformAPI.getShopWarehouses(shopId, {
          keyword: keyword || undefined,
          paged: true,
          page: pageNo,
          pageSize: size,
          gatewayRegion: listGatewayRegion,
        });
      } else if (kind === 'model') {
        res = await temuPlatformAPI.listShopModels(shopId, {
          keyword: keyword || undefined,
          modelType: modelType === '' || modelType == null ? undefined : Number(modelType),
          page: pageNo,
          pageSize: size,
        });
      } else {
        res = await temuPlatformAPI.listShopFreightTemplates(shopId, {
          keyword: keyword || undefined,
          page: pageNo,
          pageSize: size,
          gatewayRegion: listGatewayRegion,
        });
      }
      const list = Array.isArray(res?.items)
        ? res.items
        : (Array.isArray(res?.warehouses) ? res.warehouses : []);
      return { items: list, total: Number(res?.total) || list.length };
    });
    return all;
  }, [shopId, kind, keyword, modelType, listGatewayRegion]);

  const onExportCsvFiltered = async () => {
    if (!shopId) {
      showToast(needsConcreteShop ? t('syncedLib.pickConcrete') : t('returnList.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const rows = await fetchFilteredRows();
      exportCsvRows(rows);
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const onExportCsvPage = () => {
    exportCsvRows(items);
  };

  const onExportCsvSelected = () => {
    const rows = resolveSelectedRows();
    if (!rows.length) {
      showToast(t('brandLib.pickExport'), 'warning');
      return;
    }
    exportCsvRows(rows);
  };

  const onDeleteLocal = async (ids, { title } = {}) => {
    const clean = (Array.isArray(ids) ? ids : [])
      .map((id) => String(id || '').trim())
      .filter(Boolean);
    if (!shopId) {
      showToast(needsConcreteShop ? t('syncedLib.pickConcrete') : t('returnList.pickShop'), 'warning');
      return;
    }
    if (!clean.length) {
      showToast(t('syncedLib.pickDelete'), 'warning');
      return;
    }
    const ok = await showTemuConfirm({
      title: title || t('syncedLib.deleteTitle', { name: libraryLabel }),
      message: t('syncedLib.deleteMsgTemu', { count: clean.length, name: libraryLabel }),
      confirmText: t('syncedLib.deleteLocal'),
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      let deleted = clean.length;
      if (kind === 'warehouse') {
        const res = await temuPlatformAPI.batchDeleteShopWarehouses(shopId, clean);
        deleted = Number(res?.deleted ?? clean.length);
      } else if (kind === 'model') {
        const res = await temuPlatformAPI.batchDeleteShopModels(shopId, clean, { platform: false });
        deleted = Number(res?.deleted ?? clean.length);
      } else {
        const res = await temuPlatformAPI.batchDeleteShopFreightTemplates(shopId, clean);
        deleted = Number(res?.deleted ?? clean.length);
      }
      showToast(t('syncedLib.deletedLocal', { count: deleted }), 'success');
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        clean.forEach((id) => next.delete(id));
        return next;
      });
      clean.forEach((id) => selectedRowsRef.current.delete(id));
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.deleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onBatchDelete = async () => {
    await onDeleteLocal([...selectedKeys], { title: t('syncedLib.batchDeleteTitle', { name: libraryLabel }) });
  };

  const onDeleteOne = async (row) => {
    if (isOfficialModelTab) {
      showToast(t('modelLib.adminOnly'), 'info');
      return;
    }
    const key = rowKeyOf(kind, row, { official: isOfficialModelTab });
    if (!key) {
      showToast(t('syncedLib.unrecognized'), 'warning');
      return;
    }
    await onDeleteLocal([key], { title: t('syncedLib.deleteTitle', { name: libraryLabel }) });
  };

  const onCopyOfficialToPlatform = async (row) => {
    if (!shopId || !row?.id || copyingOfficialId) return;
    const ok = await showTemuConfirm({
      title: t('modelLib.copyTitle'),
      message: t('modelLib.copyMsg', { name: row.name || row.modelName || row.id }),
      confirmText: t('modelLib.copyConfirm'),
    });
    if (!ok) return;
    setCopyingOfficialId(row.id);
    try {
      const detailRes = await temuPlatformAPI.getModelTemplate(shopId, row.id, { scope: 'official' });
      const item = detailRes?.item || detailRes || row;
      const payload = (item?.payload && typeof item.payload === 'object') ? item.payload : item;
      const modelName = String(payload.modelName || item.modelName || item.name || '').trim();
      const type = Number(payload.modelType ?? item.modelType ?? modelType);
      if (!modelName) throw new Error(t('modelLib.missingName'));
      const body = {
        modelName,
        modelType: type === 1 ? 1 : 0,
      };
      const portraitSrc = String(
        payload.headPortrait || item.headPortrait || item.thumbUrl || '',
      ).trim();
      if (portraitSrc) {
        body.headPortrait = await resolvePortraitTemuUrl(shopId, portraitSrc);
      }
      if (body.modelType === 0) {
        const clothes = payload.clothesModel || {};
        body.clothesModel = {
          height: String(clothes.height || item.height || '').trim(),
          bust: String(clothes.bust || item.bust || '').trim(),
          waist: String(clothes.waist || item.waist || '').trim(),
          hipline: String(clothes.hipline || clothes.hip || item.hip || item.hipline || '').trim(),
        };
      } else {
        const shoe = payload.shoeModel || {};
        body.shoeModel = {
          footLength: String(shoe.footLength || item.footLength || '').trim(),
          footWidth: String(shoe.footWidth || item.footWidth || '').trim(),
        };
      }
      await temuPlatformAPI.createShopModel(shopId, body);
      showToast(t('modelLib.copiedToShop'), 'success');
      setModelScopeTab(TEMU_TEMPLATE_SCOPES.personal);
      setPage(1);
      await load();
    } catch (e) {
      showToast(e?.message || t('modelLib.copyToPlatformFailed'), 'error');
    } finally {
      setCopyingOfficialId(null);
    }
  };

  const runQuery = () => {
    setKeyword(keywordDraft.trim());
    setPage(1);
  };

  const resetQuery = () => {
    setKeywordDraft('');
    setKeyword('');
    setPage(1);
  };

  const onModelTypeTabChange = (nextType) => {
    const id = String(nextType ?? DEFAULT_MODEL_TYPE);
    if (id === String(modelType)) return;
    setModelType(id);
    setPage(1);
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  };

  const openCreate = () => {
    if (!allowModelMutate) return;
    if (!shopId) {
      showToast(needsConcreteShop ? t('syncedLib.pickConcrete') : t('returnList.pickShop'), 'warning');
      return;
    }
    setEditingRow(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    if (!allowModelMutate || !row) return;
    if (row.canEdit === false) {
      showToast(t('modelLib.cannotEdit'), 'warning');
      return;
    }
    setEditingRow(row);
    setModalOpen(true);
  };

  const onSaveModel = async (payload) => {
    if (!allowModelMutate || !shopId) return;
    setSaving(true);
    try {
      const modelId = editingRow?.modelId || editingRow?.id;
      if (modelId) {
        await temuPlatformAPI.updateShopModel(shopId, modelId, payload);
        showToast(t('modelLib.updated'), 'success');
      } else {
        await temuPlatformAPI.createShopModel(shopId, payload);
        showToast(t('modelLib.created'), 'success');
      }
      setModalOpen(false);
      setEditingRow(null);
      await load();
    } catch (e) {
      // 字段级错误由弹窗内红色提示展示，不再仅 toast
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => {
    if (kind === 'warehouse') {
      return [
        { key: 'warehouseId', title: idColumnLabel },
        { key: 'name', title: nameColumnLabel },
        { key: 'gatewayRegion', title: t('freightLib.region') },
        { key: 'managementType', title: t('warehouseLib.colType') },
        { key: 'status', title: t('warehouseLib.status') },
        { key: 'synced', title: t('freightLib.colSynced') },
        { key: 'ops', title: t('returnList.actions') },
      ];
    }
    if (kind === 'model') {
      const isShoe = String(modelType) === '1';
      if (isShoe) {
        return [
          { key: 'thumb', title: t('modelLib.thumb') },
          { key: 'name', title: nameColumnLabel },
          { key: 'footLength', title: t('modelLib.footLength') },
          { key: 'footWidth', title: t('modelLib.footWidth') },
          { key: 'created', title: t('productTemplate.colCreatedAt') },
          { key: 'updated', title: t('productTemplate.colUpdatedAt') },
          { key: 'ops', title: t('returnList.actions') },
        ];
      }
      return [
        { key: 'thumb', title: t('modelLib.thumb') },
        { key: 'name', title: nameColumnLabel },
        { key: 'height', title: t('modelLib.height') },
        { key: 'bust', title: t('modelLib.bust') },
        { key: 'waist', title: t('modelLib.waist') },
        { key: 'hip', title: t('modelLib.hip') },
        { key: 'created', title: t('productTemplate.colCreatedAt') },
        { key: 'updated', title: t('productTemplate.colUpdatedAt') },
        { key: 'ops', title: t('returnList.actions') },
      ];
    }
    return [
      { key: 'freightTemplateId', title: idColumnLabel },
      { key: 'name', title: nameColumnLabel },
      { key: 'gatewayRegion', title: t('freightLib.region') },
      { key: 'synced', title: t('freightLib.colSynced') },
      { key: 'ops', title: t('returnList.actions') },
    ];
  }, [kind, idColumnLabel, nameColumnLabel, modelType, t]);

  const colSpan = columns.length + 1;
  const isShoeModelTab = kind === 'model' && String(modelType) === '1';
  const modelTypeTabs = useMemo(() => ([
    { id: '0', label: t('modelLib.clothes') },
    { id: '1', label: t('modelLib.shoeTemu') },
  ]), [t]);
  const modelTypeTabLabel = modelTypeTabs.find((tab) => tab.id === String(modelType))?.label || t('modelLib.model');
  const regionFilterActive = showGatewayRegionTabs && Boolean(listGatewayRegion);

  const emptyHint = needsConcreteShop
    ? t('syncedLib.pickConcreteView')
    : (!shopId
      ? t('returnList.pickShop')
      : (loading
        ? t('returnList.loading')
        : (isTeamModelTab
          ? (keyword
            ? t('modelLib.teamNoMatch', { name: modelTypeTabLabel })
            : t('modelLib.teamEmpty', { name: modelTypeTabLabel }))
          : (isOfficialModelTab
          ? (keyword
            ? t('modelLib.officialNoMatch', { name: modelTypeTabLabel })
            : t('modelLib.officialEmpty', { name: modelTypeTabLabel }))
          : (regionFilterActive && untaggedCount > 0 && items.length === 0
            ? t('syncedLib.emptyRegionUntagged', { count: untaggedCount })
            : (syncedAt
              ? (keyword
                ? t('syncedLib.noMatch', { name: kind === 'model' ? modelTypeTabLabel : libraryLabel })
                : (regionFilterActive
                  ? t('syncedLib.emptyRegion', { name: libraryLabel })
                  : t('syncedLib.empty', { name: kind === 'model' ? modelTypeTabLabel : libraryLabel })))
              : (allowModelMutate
                ? t('modelLib.emptyCreateTemu', { name: modelTypeTabLabel })
                : t('syncedLib.emptyPullTemu', { name: libraryLabel }))))))));

  const lastSyncedLabel = isOfficialModelTab
    ? t('modelLib.officialReadonly')
    : (syncedAt
      ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(syncedAt) })
      : t('tiktokCatalog.notSynced'));

  const busy = loading || deleting || syncing || saving || exporting || copyingOfficialId != null;

  return (
    <>
      {isShopModelTab ? <TemuSyncProgressSlot kinds={syncKinds} /> : null}
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={busy}
            syncKinds={isShopModelTab ? syncKinds : []}
            quotaKind={syncKind}
            onSync={isShopModelTab ? guardedSync : undefined}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || busy || !isShopModelTab}
            lastSyncedLabel={lastSyncedLabel}
            syncHint={isOfficialModelTab ? t('modelLib.officialSyncHint') : syncHint}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || busy}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: items.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: onExportCsvFiltered,
              onExportPage: onExportCsvPage,
              onExportSelected: onExportCsvSelected,
            })}
            batchOpsMenu={isShopModelTab ? [
              {
                key: 'batch-delete',
                label: t('returnList.batchDelete'),
                tone: 'danger',
                disabled: deleting || selectedKeys.size === 0,
                onClick: onBatchDelete,
              },
            ] : []}
            trailingExtra={platformJsonDumpKind && isShopModelTab ? (
              <button
                type="button"
                className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                disabled={!shopId}
                title={shopId
                  ? t('syncedLib.platformJsonTip')
                  : t('syncedLib.pickConcrete')}
                onClick={() => setPlatformJsonOpen(true)}
              >
                {t('syncedLib.platformJson')}
              </button>
            ) : null}
            extraActions={allowModelMutate && isShopModelTab ? [
              {
                key: 'create-model',
                label: t('modelLib.create'),
                tone: 'primary',
                disabled: !shopId || busy,
                onClick: openCreate,
              },
            ] : []}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline">
              <span className="tpo-field__label">{nameColumnLabel}</span>
              <input
                className="tpo-input"
                value={keywordDraft}
                disabled={!shopId}
                placeholder={t('syncedLib.keywordPh', { name: nameColumnLabel })}
                onChange={(e) => setKeywordDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runQuery();
                }}
              />
            </div>
          </div>
          <div className="tpo-filter-bar__actions">
            <button
              type="button"
              className="tpo-btn tpo-btn--ghost tpo-btn--sm"
              disabled={!shopId || loading}
              onClick={resetQuery}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={!shopId || loading}
              onClick={runQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={listTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('productTemplate.querying')}
      >
        {kind === 'model' ? (
          <>
            <TemuTemplateScopeTabs
              scopeTab={modelScopeTab}
              canAccessTeam={canAccessTeam}
              ariaLabel={t('modelLib.scopeAria')}
              disabled={!shopId || busy}
              onChange={(next) => {
                changeModelScopeTab(next, {
                  onReset: () => {
                    setPage(1);
                    setSelectedKeys(new Set());
                    selectedRowsRef.current.clear();
                  },
                });
              }}
            />
            <TemuTemplateScopeTabs
              scopeTab={String(modelType)}
              ariaLabel={t('modelLib.typeAria')}
              tabs={modelTypeTabs}
              disabled={!shopId || busy}
              onChange={onModelTypeTabChange}
            />
          </>
        ) : null}
        <div className="tpo-drafts__table-wrap tpo-certs-table-wrap">
          <table className="tpo-publish__table tpo-drafts__table tpo-certs-table tpo-tpl-table tpo-tpl-table--synced">
            <colgroup>
              <col className="tpo-certs-col--check" />
              {columns.map((col) => (
                <col key={col.key} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tpo-certs-th tpo-certs-th--check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('productTemplate.selectAll')}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allPageSelected}
                    onChange={toggleAll}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                {columns.map((col) => (
                  <th key={col.key} scope="col" className="tpo-certs-th">{col.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!items.length ? (
                <tr>
                  <td colSpan={colSpan} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">{emptyHint}</p>
                    </div>
                  </td>
                </tr>
              ) : items.map((row) => {
                const key = rowKeyOf(kind, row, { official: isOfficialModelTab });
                const checked = selectedKeys.has(key);
                const rowEditable = allowModelMutate && isShopModelTab && row.canEdit !== false;
                const clothes = row?.payload?.clothesModel || {};
                const shoe = row?.payload?.shoeModel || {};
                const displayHeight = row.height || clothes.height;
                const displayBust = row.bust || clothes.bust;
                const displayWaist = row.waist || clothes.waist;
                const displayHip = row.hip || row.hipline || clothes.hipline || clothes.hip;
                const displayFootLength = row.footLength || shoe.footLength;
                const displayFootWidth = row.footWidth || shoe.footWidth;
                return (
                  <tr
                    key={key || `${row.name}-${Math.random()}`}
                    className="tpo-row--clickable"
                    onClick={(e) => {
                      if (e.target.closest('button, a, .temu-draft-actions')) return;
                      toggleOne(row);
                    }}
                  >
                    <td
                      className="tpo-certs-td--check"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOne(row);
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={t('returnList.selectRow', { title: row.name || key })}
                        checked={checked}
                        onChange={() => toggleOne(row)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    {kind === 'warehouse' ? (
                      <>
                        <td title={cellDisplayTitle(row.warehouseId)}>{row.warehouseId || '—'}</td>
                        <td title={cellDisplayTitle(row.name || row.warehouseName)}>{row.name || row.warehouseName || '—'}</td>
                        <td title={cellDisplayTitle(row.gatewayRegionLabel || row.gatewayRegion)}>
                          {row.gatewayRegionLabel || row.gatewayRegion || t('freightLib.unassigned')}
                        </td>
                        <td title={cellDisplayTitle(temuWarehouseManagementTypeLabel(row.managementType) || row.managementType)}>
                          {temuWarehouseManagementTypeLabel(row.managementType) || row.managementType || '—'}
                        </td>
                        <td>{row.warehouseDisable ? t('warehouseLib.disabled') : t('brandLib.available')}</td>
                        <td title={cellDisplayTitle(formatBeijingDateTime(row.lastSyncedAt || row.updatedAt))}>
                          {formatBeijingDateTime(row.lastSyncedAt || row.updatedAt) || '—'}
                        </td>
                        <td className="tpo-certs-td tpo-certs-td--ops">
                          <div className="temu-draft-actions">
                            <button
                              type="button"
                              className="wf-tasks-actions__link"
                              disabled={busy || !shopId}
                              title={t('syncedLib.deleteCacheTitle')}
                              onClick={(e) => {
                                e.stopPropagation();
                                void onDeleteOne(row);
                              }}
                            >
                              {t('returnList.delete')}
                            </button>
                          </div>
                        </td>
                      </>
                    ) : kind === 'model' ? (
                      <>
                        <td>
                          {row.headPortrait || row.thumbUrl ? (
                            <img
                              className="tpo-tpl-thumb"
                              src={row.headPortrait || row.thumbUrl}
                              alt=""
                              width={40}
                              height={40}
                            />
                          ) : '—'}
                        </td>
                        <td title={cellDisplayTitle(row.name || row.modelName)}>{row.name || row.modelName || '—'}</td>
                        {isShoeModelTab ? (
                          <>
                            <td>{displayFootLength || '—'}</td>
                            <td>{displayFootWidth || '—'}</td>
                          </>
                        ) : (
                          <>
                            <td>{displayHeight || '—'}</td>
                            <td>{displayBust || '—'}</td>
                            <td>{displayWaist || '—'}</td>
                            <td>{displayHip || '—'}</td>
                          </>
                        )}
                        <td title={cellDisplayTitle(formatBeijingDateTime(row.createdAt))}>
                          {formatBeijingDateTime(row.createdAt)}
                        </td>
                        <td title={cellDisplayTitle(formatBeijingDateTime(row.lastSyncedAt || row.updatedAt))}>
                          {formatBeijingDateTime(row.lastSyncedAt || row.updatedAt)}
                        </td>
                        <td className="tpo-certs-td tpo-certs-td--ops">
                          <div className="temu-draft-actions">
                            {isOfficialModelTab ? (
                              <button
                                type="button"
                                className="wf-tasks-actions__link"
                                disabled={busy || !shopId || copyingOfficialId === row.id}
                                title={t('modelLib.copyBtnTitle')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void onCopyOfficialToPlatform(row);
                                }}
                              >
                                {copyingOfficialId === row.id ? t('returnList.submitting') : t('modelLib.copyBtn')}
                              </button>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="wf-tasks-actions__link"
                                  disabled={busy || !rowEditable}
                                  title={rowEditable ? t('modelLib.editModel') : t('modelLib.locked')}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEdit(row);
                                  }}
                                >
                                  {t('productTemplate.edit')}
                                </button>
                                <button
                                  type="button"
                                  className="wf-tasks-actions__link"
                                  disabled={busy || !shopId}
                                  title={t('syncedLib.deleteCacheTitle')}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void onDeleteOne(row);
                                  }}
                                >
                                  {t('returnList.delete')}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td title={cellDisplayTitle(row.freightTemplateId)}>{row.freightTemplateId || '—'}</td>
                        <td title={cellDisplayTitle(row.name || row.templateName)}>{row.name || row.templateName || '—'}</td>
                        <td>
                          <div className="tpo-freight-region-cell">
                            <span title={cellDisplayTitle(row.gatewayRegionLabel || row.gatewayRegion)}>
                              {row.gatewayRegionLabel || row.gatewayRegion || t('freightLib.unassigned')}
                            </span>
                            <button
                              type="button"
                              className="wf-tasks-actions__link tpo-freight-region-cell__sites"
                              disabled={busy || !shopId}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFreightSitesRow(row);
                              }}
                            >
                              {Number(row.siteCount) > 0
                                ? t('freightLib.viewSitesCount', { count: row.siteCount })
                                : t('freightLib.viewSites')}
                            </button>
                          </div>
                        </td>
                        <td title={cellDisplayTitle(formatBeijingDateTime(row.lastSyncedAt || row.updatedAt))}>
                          {formatBeijingDateTime(row.lastSyncedAt || row.updatedAt) || '—'}
                        </td>
                        <td className="tpo-certs-td tpo-certs-td--ops">
                          <div className="temu-draft-actions">
                            <button
                              type="button"
                              className="wf-tasks-actions__link"
                              disabled={busy || !shopId}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFreightDetailId(String(row.freightTemplateId || row.id || ''));
                              }}
                            >
                              {t('freightLib.view')}
                            </button>
                            <button
                              type="button"
                              className="wf-tasks-actions__link"
                              disabled={busy || !shopId}
                              title={t('syncedLib.deleteCacheTitle')}
                              onClick={(e) => {
                                e.stopPropagation();
                                void onDeleteOne(row);
                              }}
                            >
                              {t('returnList.delete')}
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TemuOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          disabled={loading}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </ProductListCard>

      {allowModelMutate ? (
        <ModelTemplateCreateModal
          open={modalOpen}
          shopId={shopId}
          initial={editingRow}
          defaultModelType={Number(modelType)}
          busy={saving}
          onClose={() => {
            if (saving) return;
            setModalOpen(false);
            setEditingRow(null);
          }}
          onSubmit={onSaveModel}
        />
      ) : null}

      {kind === 'freight' ? (
        <FreightTemplateDetailModal
          open={Boolean(freightDetailId)}
          shopId={shopId}
          templateId={freightDetailId}
          onClose={() => setFreightDetailId('')}
        />
      ) : null}

      {kind === 'freight' ? (
        <FreightTemplateSitesModal
          open={Boolean(freightSitesRow)}
          templateName={freightSitesRow?.name || freightSitesRow?.templateName || ''}
          templateId={freightSitesRow?.freightTemplateId || freightSitesRow?.id || ''}
          regionLabel={freightSitesRow?.gatewayRegionLabel || freightSitesRow?.gatewayRegion || ''}
          sites={freightSitesRow?.sites || []}
          onClose={() => setFreightSitesRow(null)}
        />
      ) : null}

      {platformJsonDumpKind ? (
        <SyncPlatformJsonModal
          open={platformJsonOpen}
          shopId={shopId}
          dumpKind={platformJsonDumpKind}
          title={t('syncedLib.platformJsonTitle', { name: libraryLabel })}
          hint={kind === 'warehouse'
            ? t('warehouseLib.jsonHint')
            : t('syncedLib.freightJsonHint')}
          onClose={() => setPlatformJsonOpen(false)}
        />
      ) : null}
    </>
  );
}
